import {type PopoverPosition} from '@mui/material/Popover';
import {type DialogProps, type DialogTitleProps} from '@mui/material';
import {
  type Action,
  type AddDelousing,
  type HarvestDemandPutCertificatesEnum,
  type Delousing,
  type Harvest,
  type HarvestRecommendation,
  type KPIsKeyPerformanceIndicatorsApiKpiGetRequest,
  type HarvestDemandPutSpeciesEnum,
  type Transfer,
  type HarvestOverview,
  type EnrichedInputResponse,
  type IdName,
  type LiveFishTransactionResponse,
  type SiteMab,
} from 'client';
import {type FC} from 'react';
import {type AxiosError} from 'axios';
import {type TreeSelection} from 'components/Generic/DropdownTreeSelect/AssignTree';

export enum inputMenuActionType {
  openInputMenu,
  closeMenu,
  setFormData,
  closePopOver,
  openModalWindow,
  closeModalWindow,
}

export interface IFormData {
  id?: string;
  date?: Date;
  biomass?: number;
  count?: number;
  weight?: number;
  siteId?: string;
  cageId?: string;
  planId?: string;
  fullHarvest?: boolean;
  isGraded?: boolean;
  isLocked?: boolean;
  fishTypeId?: string;
  startDate?: Date;
  endDate?: Date;
  transfer?: Transfer;
  fcrModelId?: string;
  priceModelId?: string;
  qualityGradingModelId?: string;
  externalConcessionId?: string;
  actionType?: FormSelectionEnum;
  fromCompanyId?: string;
  toCompanyId?: string;
  hasExplanations?: boolean;
  explanations?: string[];
  fromFishGroupId?: string | null;
  toFishGroupId?: string | null;
  fishGroupIds?: string[];
}

export interface IPopOver {
  ownerId?: string;
  coordinates?: PopoverPosition;
  open: boolean;
}

export interface InputMenuState {
  method?: methodType;
  form?: FormSelectionEnum;
  formData?: IFormData;
  persistent?: boolean;
  popOver?: IPopOver;
  onClose?: (newEntity?: any) => void;
  modalWindow?: {
    // Could be generic if InputMenuState wasn't so legacy
    component: FC<any>;
    payload?: any;
    onClose?: (entity: any) => void;
  };
}

export interface InputMenuAction extends InputMenuState {
  type: inputMenuActionType;
  siteId?: string;
}

export enum FormSelectionEnum {
  plan = 'plan',
  site = 'site',
  cage = 'cage',
  folder = 'folder',
  fallowing = 'fallowing',
  notFallowing = 'notFallowing',
  input = 'input',
  harvest = 'harvest',
  emergencyHarvest = 'emergencyHarvest',
  concession = 'concession',
  growthModel = 'growthModel',
  fishType = 'fishType',
  siteGrowthModel = 'siteGrowthModel',
  transfer = 'transfer',
  harvestSplit = 'harvestSplit',
  smoltAvailability = 'smoltAvailability',
  inventoryData = 'inventoryData',
  priceModel = 'priceModel',
  qualityGradingsModel = 'qualityGradingsModel',
  uploadPriceModel = 'uploadPriceModel',
  fcrModel = 'fcrModel',
  timeseriesDownload = 'timeseriesDownload',
  harvestDemand = 'harvestDemand',
  liveFishTransaction = 'liveFishTransaction',
  delousing = 'delousing',
  transferOut = 'transferOut',
  transferIn = 'transferIn',
  harvestReport = 'harvestReport',
  harvestPlant = 'harvestPlant',
  harvetPlantShift = 'harvetPlantShift',
  region = 'region',
  planPublish = 'planPublish',
  priceModelAssignment = 'priceModelAssignment',
  qualityModelAssignment = 'qualityModelAssignment',
  mortalityModel = 'mortalityModel',
  mortalityModelAssignment = 'mortalityModelAssignment',
  syncPlan = 'syncPlan',
  fishGroup = 'fishGroup',
  company = 'company',
  fishGroupProperties = 'fishGroupProperties',
}

export enum methodType {
  add = 'add',
  edit = 'edit',
  delete = 'delete',
  copy = 'copy',
  import = 'import',
  explain = 'explain',
  inspect = 'inspect',
}

export interface ICage {
  volume: number;
  name: string;
  initialCount: number;
  initialWeight: number;
  inputDate: Date;
  maxDensity: number;
  maxCount: number;
  siteId: string;
  id?: string;
  fishTypeId: string;
  companyId: string;
  species: HarvestDemandPutSpeciesEnum;
  certificates: HarvestDemandPutCertificatesEnum[];
  fishGroupName: string | null;
  fishGroupInventoryId: string | null;
  fishGroupPropertyIds: string[];
  planId: string;
  accumulatedBiomassHarvested: number;
  accumulatedBiomassLost: number;
}

export interface InputsState {
  id?: string;
  planId: string;
  treeSelection: TreeSelection;
  date: Date;
  count: number;
  weight: number;
  fishTypeId: string;
  companyId: string;
  species: HarvestDemandPutSpeciesEnum;
  certificates: HarvestDemandPutCertificatesEnum[];
  smoltSupplyId: string | null;
  fishGroupName: string | null;
  fishGroupPropertyIds: string[];
  isLocked?: boolean;
}

export interface HarvestInitialState {
  cageId: string;
  siteId?: string;
  biomass: number;
  allocatedBiomass: number;
  allocatedCount: number;
  originalDate: Date;
  count: number;
  weight: number;
  date: Date | null;
  harvestPlantId?: string | null;
  fullHarvest: boolean;
  isGraded: boolean;
  isSequence: boolean;
  datesSequence: Date[];
  isEmergency?: boolean;
  id?: string;
}

export interface FallowingInitialState {
  startDate: Date | null;
  endDate: Date | null;
  siteId: string;
}

export enum URLPathname {
  root = '/',
  plan = '/plan',
  planning = '/plan/planning',
  companies = '/plan/companies',
  concessions = '/plan/concessions',
  events = '/plan/events',
  fishGroups = '/plan/fishGroups',
  fishGroupProperties = '/plan/fishGroupProperties',
  notFound = '/notFound',
  connectionsSettings = '/connectionsSettings',
  harvestReports = '/harvestReports',
  optimizationSettings = '/plan/optimizationSettings',
  planModels = '/plan/planModels',
  planModelsQualityGradings = '/plan/planModels/qualityGradings',
  planModelsMortalityModels = '/plan/planModels/mortalityModels',
  planModelsGrowthModels = '/plan/planModels/growthModels',
  planModelsStarvationModel = '/plan/planModels/starvationModel',
  planModelsHarvestLossModel = '/plan/planModels/harvestLossModel',
  planModelsFCRModel = '/plan/planModels/fcrModel',
  optimizationRuns = '/optimizationRuns',
  optimizationRunDetail = '/optimizationRunDetail',
  shortTermPlanning = '/plan/shortTermPlanning',
  smoltSupply = '/plan/smoltSupply',
  planHistory = '/plan/planHistory',
  planHarvestDemand = '/plan/planHarvestDemand',
  fishTypes = '/plan/fishTypes',
  liveFishTransactions = '/plan/liveFishTransactions',
  privacyPolicy = '/info/privacyPolicy',
  termsofService = '/info/termsofService',
  overviewTable = '/plan/tableView',
  harvestPlant = '/plan/harvestPlant',
  organizationOverview = '/plan/organizationOverview',
  pivotTable = '/plan/pivotTable',
  planVersions = '/plan/versions',
  planOverview = '/plan/overview',
  planOverviewInputs = '/plan/overview/inputs',
  planOverviewExplanations = '/plan/overview/explanations',
  planSynchronization = '/plan/planSynchronization',

  error = '/error',
}

export enum URLParams {
  site = 'site',
  selectedSite = 'selectedSite',
  cage = 'cage',
  plan = 'plan',
  comparePlan = 'comparePlan',
  language = 'lang',
  run = 'run',
  contextCageId = 'contextCageId',
  shortTermResolution = 'shortTermResolution',
  longTermResolution = 'longTermResolution',
  tableResolution = 'tableResolution',
  growthTag = 'growthTag',
  fishGroupProperties = 'fishGroupProperties',
  liceTag = 'liceTag',
  mortalityTag = 'mortalityTag',
  shortTermConcessionChart = 'shortTermConcessionChart',
  filteredAction = 'filteredAction',
  siteTags = 'siteTags',
  newId = 'newId',
  speciesTag = 'speciesTag',
  certificateTag = 'certificateTag',
  harvestPlantTag = 'harvestPlantTag',
  qualityDistributionTag = 'qualityDistributionTag',
  qualityDistributionTagSuperior = 'qualityDistributionTagSuperior',
  qualityDistributionTagProduction = 'qualityDistributionTagProduction',
  qualityDistributionTagOrdinary = 'qualityDistributionTagOrdinary',
  qualityDistributionTagVrak = 'qualityDistributionTagVrak',
  fullscreen = 'fullscreen',
  popUpType = 'popUpType',
  startDate = 'startDate',
  endDate = 'endDate',
}

export enum Theme {
  dark = 'dark',
  light = 'light',
}

export enum SupportedLanguages {
  norwegian = 'no',
  english = 'en',
  spanish = 'es-CL',
}

export interface IKPIs {
  name: string;
  id: string;
  index: number;
  harvestVolumeHog: number;
  harvestVolumeLwe: number;
  averageHarvestWeight: number;
  mabUtilization: number;
  turnover: number;
}

// It should be BE enum
export enum HarvestTimelineColumnsEnum {
  biomass = 'biomass',
  biomassSalmon = 'biomassSalmon',
  biomassTrout = 'biomassTrout',
}

export enum ColumnFieldEnum {
  days = 'days',
  totalHarvestVolume = 'totalHarvestVolume',
  averageHarvestWeight = 'averageHarvestWeight',
  harvestSizeDistribution = 'harvestSizeDistribution',
  averageGrowthPerDay = 'averageGrowthPerDay',
  MABUtilization = 'MABUtilization',
  expectedRevenue = 'expectedRevenue',
  biomass = 'biomass',
  MAB = 'MAB',
  count = 'count',
  weight = 'weight',
  capacity = 'capacity',
  feedAmount = 'feedAmount',
  producedBiomass = 'producedBiomass',
}

export enum KPIColumnEnum {
  averageHarvestWeight = 'averageHarvestWeight',
  closingBiomass = 'closingBiomass',
  days = 'days',
  feedAmount = 'feedAmount',
  feedCost = 'feedCost',
  netGrowth = 'netGrowth',
  harvestSizeDistribution = 'harvestSizeDistribution',
  harvestVolumeHog = 'harvestVolumeHog',
  harvestVolumeLwe = 'harvestVolumeLwe',
  mabUtilization = 'mabUtilization',
  turnover = 'turnover',
  harvestCount = 'harvestCount',
}

export declare type PlanKPIs = KPIsKeyPerformanceIndicatorsApiKpiGetRequest['columns'];

export enum SelectableKPIs {
  index = 'index',
  totalHarvestVolume = 'totalHarvestVolume',
  MABUtilization = 'MABUtilization',
  averageHarvestWeight = 'averageHarvestWeight',
  expectedRevenue = 'expectedRevenue',
}

export enum ResolutionOverviewEnum {
  hour = 'hour',
  day = 'day',
  week = 'week',
  month = 'month',
  year = 'year',
}

export enum ResolutionEnum {
  day = 'day',
  week = 'week',
  month = 'month',
  year = 'year',
}

export interface IDateRange {
  startDate: Date;
  endDate: Date;
}

interface PlanEvent {
  id: string;
  date: Date;
  actionType: FormSelectionEnum;
  siteName: string;
  siteId: string;
  cageName: string;
  cageId: string;
  biomass?: number | undefined;
  count?: number;
  weight: number;
}

enum ContainerEnum {
  open = 'open',
  close = 'close',
}

export declare type CellActionType =
  | FormSelectionEnum.harvest
  | FormSelectionEnum.input
  | FormSelectionEnum.transfer
  | FormSelectionEnum.delousing
  | FormSelectionEnum.liveFishTransaction
  | FormSelectionEnum.emergencyHarvest;

export interface EventsRowData {
  id: string;
  date: string;
  dateTime: number;
  cellType: CellActionType;
  actionType: FormSelectionEnum;
  actionTypeRaw: string;
  siteName: string;
  siteId: string;
  cageName: string;
  cageId: string;
  biomass: string;
  biomassNumber: number;
  count: string;
  countNumber: number;
  weight: string;
  weightNumber: number;
  actions: string;
  isLocked: boolean;
  fishTypeId?: string;
  fullHarvest?: boolean | null;
  fishTypeName: string;
  species: string | null;
  certificates?: string;
  regionName: string;
  fromFishGroupId?: string | null;
  toFishGroupId?: string | null;
  explanations?: string[];
  fishGroupProperties: IdName[];
}
interface HeadCell {
  id: keyof EventsRowData;
  disablePadding: boolean;
  numeric: boolean;
}
export type Order = 'asc' | 'desc';

export enum ITTTVersionEnum {
  vest = 'vest',
  midt = 'midt',
  nord = 'nord',
  nord2 = 'nord2',
  orret = 'orret',
}

export enum LockedEnum {
  locked = 'locked',
  unLocked = 'unLocked',
}

export interface ModalDialogProps<ReturnData, FormPayload> {
  dialogProps: DialogProps;
  dialogTitleProps: DialogTitleProps;
  modalComponentProps: {
    onBeforeClose?: (returnData?: ReturnData) => void;
    formDataPayload: FormPayload;
  };
}

interface ModalDialogProps2 {
  dialogProps: DialogProps;
  dialogTitleProps: DialogTitleProps;
}

export enum SWRKeyEnum {
  GET_GLOBAL_GROWTH_MODELS,

  GET_PLAN_DETAILED,
  GET_PLAN,
  GET_PLANS,
  GET_PLAN_SITE_GROWTH_MODEL_ASSIGNMENT,
  GET_PLAN_FOLDERS,
  GET_PLAN_HARVESTS,
  GET_PLAN_INPUTS,
  GET_PLAN_OR_SMOLT_SUPPLY_INPUTS,
  GET_UNASSIGNED_INPUTS_TO_SMOLT_SUPPLY,
  GET_PLAN_TREE_NAVIGATION,
  GET_PLAN_SITES,
  GET_PLAN_FISH_TYPES,
  GET_PLAN_FISH_TYPES_GROWTH_MODELS,
  GET_PLAN_CAGES,
  GET_PLAN_GROWTH_MODELS,
  GET_GROWTH_MODEL,
  GET_PLAN_HARVESTS_RECOMMENDATION,
  GET_PLAN_ACTIONS,
  GET_PLAN_HARVESTS_WITH_DISTRIBUTIONS,
  GET_PLAN_CONCESSIONS,
  GET_PLAN_CONCESSION,
  GET_PLAN_SMOLT_SUPPLY,
  GET_PLAN_HISTORY,
  GET_PLAN_ACTIVE_STATUS_POLLING,
  GET_PLAN_OPTIMIZATION_STATE_POLLING,
  GET_PLAN_OPTIMIZATION_STATE,
  GET_PLAN_LIVE_FISH_TRANSACTIONS,
  GET_PLAN_LIVE_FISH_TRANSACTION,
  GET_PLAN_REGIONS,
  GET_PLAN_SETTINGS,
  GET_PLAN_MORTALITY_MODEL,
  GET_PLAN_MORTALITY_MODELS,
  GET_PLAN_MORTALITY_MODEL_ASSIGNMENTS,
  GET_PLAN_TRANSFERS,
  GET_PLAN_FISH_GROUPS,
  GET_PLAN_FISH_GROUP_PROPERTIES,
  GET_FISH_GROUP,
  GET_PLANS_KPIs,
  GET_PLANS_HARVEST_VOLUME,
  GET_PLANS_INPUT_VOLUME,
  GET_SITE,
  GET_SITE_CAGES,
  GET_SITE_TRANSFERS,
  GET_SITE_EMPTY_CAGES,
  GET_SITE_NONEMPTY_CAGES,
  GET_SITE_FALLOWINGS,
  GET_PLAN_FALLOWINGS,
  GET_SITE_HARVESTS,
  GET_SITE_INPUTS,
  GET_SITE_ACTIONS,
  GET_CAGE,
  GET_CAGE_INPUTS,
  GET_CAGE_HARVESTS,
  GET_CAGE_FISH_TYPES,
  GET_CAGES_GROWTH,
  GET_CAGE_FORECAST,
  GET_CAGES_MORTALITY,
  GET_GLOBAL_SETTINGS,
  GET_FALLOWING,
  GET_TRANSFER,
  GET_HARVEST,
  GET_HARVEST_QUALITY_DISTRIBUTION,
  GET_CAGE_QUALITY_DISTRIBUTION_FORECAST,
  GET_INPUT,
  GET_MAB_UTILIZATION_PER_PLAN,
  GET_MAB_UTILIZATION_PER_CONCESSION,
  GET_SITE_CAGE_FORECAST,
  GET_PLAN_FORECAST,
  GET_INVENTORY_DATA_STATS,
  GET_SENSOR_DATA,
  GET_SMOLT_SUPPLY,
  GET_PRICE_MODEL,
  GET_PRICE_MODELS,
  GET_PRICE_MODELS_ROWS,
  GET_PRICE_MODELS_TYPE,
  GET_PRICE_MODELS_TYPES,
  GET_PRICE_MODELS_ASSIGNMENTS,
  GET_FCR_MODELS,
  GET_FCR_MODEL,
  GET_FCR_MODEL_ROWS,

  GET_GET_TENANTS,
  GET_TENANT,
  GET_ME,
  GET_API_VERSION,
  GET_HARVEST_DEMAND,
  GET_HARVEST_DEMANDS,
  GET_ESTIMATED_NET_VALUE,
  GET_LICE,
  GET_LICE_SOURCES,
  GET_ACTIVE_LICE_SOURCES,
  GET_SITE_STATUS,
  GET_NOTIFICATIONS,
  GET_CAGE_DELOUSINGS,
  GET_CAGE_LIVE_FISH_TRANSACTIONS,
  GET_CAGE_LEGACY_LIVE_FISH_TRANSACTIONS,
  GET_SITE_DELOUSINGS,
  GET_DELOUSING,
  GET_HARVEST_PLANTS,
  GET_HARVEST_PLANT,
  GET_HARVEST_PLANT_SHIFT,
  GET_HARVESTS_PLANTS_CAPACITY,
  GET_HARVESTS_PLANTS_AVAILABLE,
  GET_OPTIMIZATION_RUNS,
  GET_PLAN_OPTIMIZATION_RUNS,
  GET_SITE_TEMPERATURE,
  GET_CAGES_TEMPERATURE,
  GET_CAGES_WITH_UNALLOCATED_BIOMASS,
  GET_HARVEST_REPORTS,

  GET_QUALITY_GRADING,
  GET_QUALITY_GRADINGS,
  GET_SITE_QUALITY_GRADING_MODELS_OVERVIEW_TABLE,
  GET_CAGE_QUALITY_GRADING_MODELS_OVERVIEW_TABLE,
  GET_QUALITY_MODEL_ASSIGNMENTS,

  GET_DATA_SOURCE_CONNECTIONS,
  GET_DATA_SOURCE_CONNECTION_STATE,
  GET_GROWTH_MODELS_TYPES,
  GET_CAGE_TEMPERATURE,
  GET_CAGE_TIME_SERIES,
  GET_PLAN_SYNCHRONIZATION_DRAFT,

  GET_PLAN_COMPANIES,
  GET_PLAN_COMPANY,
  GET_SITE_CONCESSIONS,
}

export interface MultipleTransfers<T extends Transfer = Transfer> {
  in: T[];
  out: T[];
}

export interface HarvestRecommendationProcessed extends HarvestRecommendation {
  dateObject: Date;
  isLocked: undefined;
  cellActionType: FormSelectionEnum;
  certificates?: undefined;
}

export interface ActionProcessed extends Action {
  fishGroupProperties: IdName[];
  toFishGroupId: string | undefined;
  fromFishGroupId: string | undefined;
  dateObject: Date;
  cellActionType: CellActionType;
}
export interface HarvestProcessed extends Harvest {
  dateObject: Date;
}

export interface HarvestOverviewProcessed extends HarvestOverview {
  cellActionType: CellActionType;
  dateObject: Date;
  endOfMonth: Date;
  endOfYear: Date;
  endOfWeek: Date;
}

export interface InputProcessed extends EnrichedInputResponse {
  dateObject: Date;
  endOfWeek: Date;
  endOfMonth: Date;
  endOfYear: Date;
}

export interface TransferProcessed extends Transfer {
  dateObject: Date;
}

export interface CageTransferProcessed extends TransferProcessed {
  cageId: string;
}
export interface LiveFishTransactionProcessed extends LiveFishTransactionResponse {
  dateObject: Date;
}

export interface DelousingProcessed extends Delousing {
  dateObject: Date;
}
export interface AddDelousingProcessed extends AddDelousing {
  dateObject: Date;
  siteId: string;
  id: string;
  stringedMortalityRatio?: string;
}

export enum ChartTypeEnum {
  bar = 'bar',
}

export enum WeightUnits {
  hog = 'hog',
  lwe = 'lwe',
}

export * from 'interfaces/reducers';

export interface PlanMortalityStarvation {
  species: HarvestDemandPutSpeciesEnum;
  starvation: number;
}

/**
 * must be serializable for deep copy
 */
export interface TreeNode {
  id: string;
  name: string;
  children?: TreeNode[];
}

export type PlanType = 'harvest' | 'production';

export type AppAxiosError = AxiosError<{errors?: string}>;

export interface IQualityGrading {
  production: number;
  vrak: number;
  ordinary: number;
  superior: number;
}

export interface SiteMABProcessed extends SiteMab {
  fromDateObject: Date;
}
